import type {
  BillBand,
  BillCount,
  FaIncomeInput,
  FaRead,
} from "@medbillai/schemas";
import type {
  NateraIntakePlaybookVariant,
  NateraIntakeStoryVariant,
} from "@medbillai/utils/flag";

import type { Feeling, StressLevel } from "./persona";

export type {
  BillBand,
  BillCount,
  FaIncomeInput,
  FaRead,
  NateraIntakePlaybookVariant,
  NateraIntakeStoryVariant,
};

// Milestone types, composed with `&` into each screen's `needs`. The flow engine
// accumulates them forward, so a screen declares everything collected up to it.

export type BillCountData = {
  billCount: BillCount;
};

export type BillData = BillCountData & {
  /** The range the member picked; explicit null when they skipped it. */
  billBand: BillBand | null;
};

/** Which bill brought the member in. Only a Natera bill continues past validation. */
export const BILL_SOURCES = ["natera", "other-lab", "something-else"] as const;
export type BillSource = (typeof BILL_SOURCES)[number];

export type BillSourceData = {
  billSource: BillSource;
};

export type StressData = {
  stressLevel: StressLevel;
};

export type FeelingData = {
  feeling: Feeling;
};

/** Everything the persona layer collects, composed into the screens downstream of it. */
export type EmotionalData = StressData & FeelingData;

/**
 * The playbook experiment arm, raw, so a resumed session stays in it. `null` when
 * the member passed the fork before flags were ready and so stays unenrolled;
 * absent before the fork.
 */
export type PlaybookArmData = {
  playbookArm?: NateraIntakePlaybookVariant | null;
};

/** The story experiment arm, with the same null/absent meaning as `PlaybookArmData`. */
export type StoryArmData = {
  storyArm?: NateraIntakeStoryVariant | null;
};

/** Where the hero questions were answered. */
export type LandingEntry = "offsite" | "onsite" | "routing";

export type LandingEntryData = {
  landingEntry?: LandingEntry;
};

export type HouseholdData = {
  householdSize: number;
};

export type IncomeData = {
  income: FaIncomeInput;
};

export type FaReadData = {
  faRead: FaRead;
};

export type StoryData = {
  /** Explicit null when skipped — the packet never omits a skipped answer. */
  story: string | null;
};
